import {
  ADMIN_DELIVERIES_DETAILS,
  ADMIN_ORDER_DETAILS,
  ArrowRed,
  Div,
  H5,
  Icon,
  Img,
  Modal,
  P,
  PropsModal,
  Separator,
  StatusCircle,
  TooltipIcon,
  VGrubsLogoSmall,
  VOrdersLogoSmall,
  bytesLogo,
  deliveryLogo,
  displayCentsInDollars,
  isAdmin,
  kanekt365Logo,
  lightTheme,
  renderIncomeStatusName,
  vGrubsAILogo,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import {
  AgentIncomeStatus,
  AgentIncomeType,
  AgentTransactionsType,
  IncomeType,
  NewTransactionItem,
} from 'components/src/types/api/wallet.types';
import {
  describeAgentIncome,
  describeAgentRow,
  showAgentStatusColor,
} from '../TransactionAgentComponents';

import { openInNewTab } from '../helpers';

export const IncomeDetailAgentModal = ({
  modalIsOpen,
  closeModal,
}: PropsModal & {
  modalIsOpen: NewTransactionItem | null;
}) => {
  const transaction = modalIsOpen?.object;

  const isBalanceCorrection =
    transaction?.type === AgentIncomeType.balanceIncrementation ||
    transaction?.type === AgentIncomeType.balanceDeduction;

  const descriptionLabel = describeAgentIncome(transaction?.type);

  const { sign, amountColor } = transaction
    ? describeAgentRow(transaction, AgentTransactionsType.incomes)
    : { sign: '+', amountColor: lightTheme.green };

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      textAlign={'left'}
      header={'Income Details'}
      zIndex={10}
    >
      <P $m="0">Date & Time</P>
      <H5 $m="0">
        {DateTime.fromISO(transaction?.createdAt || '').toFormat(
          'MMM dd, yyyy - hh:mm a'
        )}
      </H5>

      {transaction?.orderId && (
        <>
          <Separator $m="16px 0" />
          <Div $flex $alignCenter $between>
            <Div>
              <P $m="0">Order ID</P>
              <H5 $m="0">#{transaction.orderId}</H5>
            </Div>
            <Div
              $pointer
              onClick={() =>
                openInNewTab(
                  `${ADMIN_ORDER_DETAILS.replace(
                    ':id',
                    transaction.orderId?.toString() || ''
                  )}`
                )
              }
            >
              <Icon>
                <ArrowRed />
              </Icon>
            </Div>
          </Div>
        </>
      )}

      {transaction?.orderVDriveId && (
        <>
          <Separator $m="16px 0" />
          <Div $flex $alignCenter $between>
            <Div>
              <P $m="0">Delivery ID</P>
              <H5 $m="0">#{transaction.orderVDriveId}</H5>
            </Div>
            <Div
              $pointer
              onClick={() =>
                openInNewTab(
                  `${ADMIN_DELIVERIES_DETAILS.replace(
                    ':id',
                    transaction.orderVDriveId?.toString() || ''
                  )}`
                )
              }
            >
              <Icon>
                <ArrowRed />
              </Icon>
            </Div>
          </Div>
        </>
      )}

      {transaction?.storeName &&
        !transaction?.orderId &&
        !transaction?.orderVDriveId && (
          <>
            <Separator $m="16px 0" />
            <Div $flex $alignCenter $between>
              <Div>
                <P $m="0">Store Name</P>
                <H5 $m="0">{transaction.storeName}</H5>
              </Div>
            </Div>
          </>
        )}

      <Separator $m="16px 0" />
      <Div $flex $alignCenter $between>
        <Div>
          <P $m="0">Description</P>
          <H5 $m="0">{descriptionLabel}</H5>
        </Div>
        <Icon>
          {transaction?.type === AgentIncomeType.vGrubs && <VGrubsLogoSmall />}
          {transaction?.type === AgentIncomeType.vOrders && (
            <VOrdersLogoSmall />
          )}
          {(transaction?.type as string) === IncomeType.bytes && (
            <Img alt="" $height="40px" src={isAdmin ? bytesLogo : vGrubsAILogo} />
          )}
          {(transaction?.type as string) === IncomeType.kanekt365 && (
            <Img
              alt=""
              $height="40px"
              src={isAdmin ? kanekt365Logo : vGrubsAILogo}
            />
          )}
          {(transaction?.type as string) === IncomeType.dcom && (
            <Img alt="" $height="40px" src={deliveryLogo} />
          )}
        </Icon>
      </Div>

      {isBalanceCorrection && transaction?.reason && (
        <>
          <Separator $m="16px 0" />
          <Div>
            <P $m="0">Reason</P>
            <H5 $m="0" style={{ whiteSpace: 'pre-line' }}>
              {transaction.reason}
            </H5>
          </Div>
        </>
      )}

      <Separator $m="16px 0" />
      <P $m="0">Status</P>

      <Div $flex $alignCenter $gap="8px">
        <StatusCircle
          $color={showAgentStatusColor(transaction?.status || undefined)}
        />
        <H5 $m="0" $capitalize>
          {renderIncomeStatusName(transaction?.status ?? '')}
        </H5>
        {transaction?.status !== AgentIncomeStatus.available && (
          <TooltipIcon
            tooltip={
              transaction?.availableAt
                ? transaction.type !== AgentIncomeType.balanceDeduction
                  ? `Will be paid out to agents vPay Card on ${DateTime.fromISO(
                      transaction.availableAt
                    ).toFormat('MMM dd, yyyy')}.`
                  : `Will be deducted from agents vPay Card on ${DateTime.fromISO(
                      transaction.availableAt
                    ).toFormat('MMM dd, yyyy')}.`
                : ''
            }
          />
        )}
      </Div>

      <Separator $m="16px 0" />
      <Div $flex $alignCenter $between>
        <H5 $m="0">Total Amount</H5>
        <H5 $color={amountColor} $m="0">
          {sign}
          {displayCentsInDollars(transaction?.amount || 0)}
        </H5>
      </Div>
    </Modal>
  );
};
